import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { isBuilderPlan as isBuilderPlanAccount } from '@/services/auth/plan-slugs';
import { PlanFeature, type AugmentedAccount, type AugmentedUser } from '@/services/auth/types';
import type { ProductTaxonomy } from '@/services/product-taxonomy';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { isEmptyString } from '@/utils/string';
import type { usePermissions } from '@customerio/hydra-permissions/react';
import type { IconName } from '@customerio/pluma-icons';
import { iconNames } from '@customerio/pluma-icons';
import type { NavItemProps as PlumaNavItemProps } from '@customerio/pluma-components/react';
import type { MergedWorkspace, WorkspaceObjectType } from '../../../../features/workspaces';
import { AppBundle, AppBundleRoutes } from '../../../../utils/app-bundles';

export interface NavItemGroup {
  title?: string;
  items: NavItem[];
}

type PlumaNavItemVisibilityProps = Pick<PlumaNavItemProps, 'isVisibleWhenDeselected' | 'isVisibleWhenSelected'>;

export interface NavItem extends PlumaNavItemVisibilityProps {
  activateOnPipelinesRoutes?: string[];
  activeExceptUrls?: string[];
  activeWhenUrls?: string[];
  ariaLabel?: string;
  children?: NavItem[];
  href?: string;
  iconName?: IconName;
  id: string;
  isCustomizable?: boolean;
  isDropdownMenu?: boolean;
  isGreyedOut?: boolean;
  isHiddenByDefault?: boolean;
  isDisabled?: boolean;
  isMoreMenuOnly?: boolean;
  isVisuallyDisabled?: boolean;
  disabledTooltip?: string;
  tooltip?: string;
  label: string;
  showNewLabel?: boolean;
  showPremiumLabel?: boolean;
  defaultIsOpen?: boolean;
  transitionOptions?: TargetTransitionOptions;
}

const defined = <T>(item: T | null): item is T => item !== null;

function generateConfigureDataGroup(params: {
  isBuilderPlan: boolean;
  isGeoFencingEnabled: boolean;
  envPrefix: string;
  envAppPrefix: string;
  collectionsShowPremiumLabel: boolean;
  hasPermission: ReturnType<typeof usePermissions>['hasPermission'];
  hasAnyPermission: ReturnType<typeof usePermissions>['hasAnyPermission'];
}): NavItemGroup {
  const {
    isBuilderPlan,
    isGeoFencingEnabled,
    envPrefix,
    envAppPrefix,
    collectionsShowPremiumLabel,
    hasPermission,
    hasAnyPermission,
  } = params;

  const geofencesItem = isGeoFencingEnabled
    ? {
        id: 'geofences',
        label: 'Geofences',
        href: `${envPrefix}/geofences`,
        iconName: 'pin' as const,
      }
    : null;

  if (isBuilderPlan) {
    return {
      title: 'Configure data',
      items: [
        {
          id: 'data-index',
          label: 'Data Index',
          href: `${envAppPrefix}/data-index`,
          iconName: 'data-index' as const,
        },
        hasPermission('journeys/imports/view') === true
          ? {
              id: 'imports',
              label: 'Imports',
              href: `${envAppPrefix}/imports`,
              iconName: 'import' as const,
            }
          : null,
        hasPermission('journeys/exports/view') === true
          ? {
              id: 'exports',
              label: 'Exports',
              href: `${envAppPrefix}/exports`,
              iconName: 'export' as const,
            }
          : null,
        geofencesItem,
      ].filter(defined),
    };
  }

  return {
    title: 'Configure data',
    items: [
      {
        id: 'data-index',
        label: 'Data Index',
        href: `${envAppPrefix}/data-index`,
        iconName: 'data-index' as const,
      },
      hasAnyPermission('journeys/imports/view', 'journeys/exports/view') === true
        ? {
            id: 'integrations',
            label: 'Integrations',
            href: `${envAppPrefix}/integrations`,
            activateOnPipelinesRoutes: ['/pipelines'],
            iconName: 'integrations' as const,
          }
        : null,
      geofencesItem,
      hasPermission('journeys/imports/view') === true
        ? {
            id: 'imports',
            label: 'Imports',
            href: `${envAppPrefix}/imports`,
            iconName: 'import' as const,
            isHiddenByDefault: true,
          }
        : null,
      hasPermission('journeys/exports/view') === true
        ? {
            id: 'exports',
            label: 'Exports',
            href: `${envAppPrefix}/exports`,
            iconName: 'export' as const,
            isHiddenByDefault: true,
          }
        : null,
      {
        id: 'collections',
        label: 'Collections',
        href: `${envAppPrefix}/collections`,
        showPremiumLabel: collectionsShowPremiumLabel,
        iconName: 'database' as const,
        isHiddenByDefault: true,
      },
    ].filter(defined),
  };
}

function shouldShowFullJourneysRoutesMenu(auth: { account: AugmentedAccount | null; user: AugmentedUser | null }) {
  const { user, account } = auth;

  if (user == null || account == null) {
    return false;
  }

  // if masqueraded into an account
  if (account?.is_masq ?? false) {
    // if the user doesn't have all permissions properly set to masq return false
    if (user.noMasqAccess || user.masqLevelNotSet) {
      return false;
    }

    // if the user has masq permissions and accessGrantedUntil is set return true
    if (user.isSuperUser || user.canReadWriteMasq || user.canReadOnlyMasq) {
      return true;
    }

    return false;
  }

  return true;
}

export function generateRoutes(
  currentWorkspace: MergedWorkspace,
  auth: { account: AugmentedAccount | null; user: AugmentedUser | null },
  { hasAnyPermission, hasPermission }: ReturnType<typeof usePermissions>,
  options: {
    taxonomy: ProductTaxonomy;
    objectTypes?: WorkspaceObjectType[];
  },
): NavItemGroup[] {
  if (currentWorkspace.cio_workspace_id == null || currentWorkspace.cio_workspace_id === 0) {
    return [];
  }

  const { taxonomy, objectTypes = [] } = options;
  // oxlint-disable-next-line local-rules/enforce-generate-path -- prefix used to build many nav URLs
  const envPrefix = `/workspaces/${currentWorkspace.id}`;
  const envAppPrefix = `${envPrefix}${AppBundleRoutes[AppBundle.Journeys]}`;

  // Campaigns → Automations rename: the label and URL segment both come from the
  // product taxonomy catalog (flag-gated), not hardcoded here. See
  // services/product-taxonomy/renames/automations.ts.
  const campaignsRoute = taxonomy.route('campaigns.index');
  const campaignsSegment = campaignsRoute.slice(campaignsRoute.lastIndexOf('/') + 1);
  // Only the future variant gets the rename hint, so existing users can map the
  // new "Automations" label back to "Campaigns". See FPS-2398.
  const campaignsRenameTooltip =
    taxonomy.variant === 'future' ? 'Automations (formerly called Campaigns) - workflow led messaging.' : undefined;

  // Analysis → Reports rename: the label and URL segment both come from the product
  // taxonomy catalog (flag-gated), not hardcoded here. See
  // services/product-taxonomy/renames/reports.ts.
  const analysisRoute = taxonomy.route('analysis.index');
  const analysisSegment = analysisRoute.slice(analysisRoute.lastIndexOf('/') + 1);

  // People → Profiles rename: the label and URL segment both come from the product
  // taxonomy catalog (flag-gated), not hardcoded here. See
  // services/product-taxonomy/renames/profiles.ts.
  const peopleRoute = taxonomy.route('people.index');
  const peopleSegment = peopleRoute.slice(peopleRoute.lastIndexOf('/') + 1);

  // Activity Logs → Profile activity rename: the label and URL both come from the product
  // taxonomy catalog (flag-gated), not hardcoded here. See
  // services/product-taxonomy/renames/profile-activity.ts. Unlike Campaigns, the future
  // segment is multi-part (profile_activity/identified), so resolve the full href rather
  // than a single trailing segment.
  const profileActivityRoute = taxonomy.route('profileActivity.index');
  const profileActivityHref = profileActivityRoute.replace(':workspaceId', String(currentWorkspace.id));
  // The href points at the default (identified) tab in the future taxonomy, so match on the
  // base segment instead — keeps the nav item highlighted across all sub-tabs (identified /
  // anonymous) and the base path, the way the flag-off `/activity_logs` prefix used to.
  const profileActivityBaseHref = `${envAppPrefix}/${profileActivityRoute.split('/journeys/')[1].split('/')[0]}`;

  const showFullMenu = shouldShowFullJourneysRoutesMenu(auth);
  const isAnonymousInAppEnabled = auth.account?.planFeatures[PlanFeature.AnonymousInApp].enabled ?? false;

  const isAiEnabled = hasFeatureFlag(AccountFeatureFlag.AiEnabled);
  const isBuilderPlan = isBuilderPlanAccount(auth.account);
  const isGeoFencingEnabled = hasFeatureFlag(AccountFeatureFlag.GeoFencingEnabled);
  if (!showFullMenu) {
    return [];
  }

  return (
    [
      {
        items: [
          {
            id: 'dashboard',
            label: 'Home',
            href: `${envPrefix}/home`,
            iconName: 'home',
            activeWhenUrls: [`${envPrefix}/home`, `${envAppPrefix}/dashboard`, `${envAppPrefix}/home`],
          },
          isAiEnabled
            ? {
                id: 'agent',
                label: 'Agent',
                href: `${envPrefix}/agent`,
                iconName: 'sparkle',
              }
            : null,
        ],
      },
      isBuilderPlan
        ? {
            title: 'Review',
            items: [
              {
                id: 'logs',
                label: taxonomy.t('nav.review.activityLogs'),
                href: profileActivityHref,
                activeWhenUrls: [profileActivityBaseHref],
                iconName: 'activity',
              },
              {
                id: 'outbox',
                label: 'Deliveries',
                href: `${envAppPrefix}/outbox`,
                iconName: 'delivery-log',
              },
            ],
          }
        : {
            title: 'Review',
            items: [
              {
                id: 'goals',
                label: 'Goals',
                href: `${envAppPrefix}/goals`,
                iconName: 'goal',
              },
              {
                id: 'analysis',
                label: taxonomy.t('nav.review.analysis'),
                href: `${envAppPrefix}/${analysisSegment}`,
                iconName: 'analysis',
              },
              {
                id: 'logs',
                label: taxonomy.t('nav.review.activityLogs'),
                href: profileActivityHref,
                activeWhenUrls: [profileActivityBaseHref],
                iconName: 'activity',
              },
              {
                id: 'outbox',
                label: 'Deliveries & Drafts',
                href: `${envAppPrefix}/outbox`,
                iconName: 'delivery-log',
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Send messages',
            items: [
              {
                id: 'broadcasts',
                label: 'Broadcasts',
                href: `${envAppPrefix}/broadcasts`,
                iconName: 'broadcast',
              },
              {
                id: 'transactional',
                label: 'Transactional',
                href: `${envAppPrefix}/transactional`,
                iconName: 'transactional-api',
              },
            ],
          }
        : {
            title: 'Send messages',
            items: [
              {
                id: 'campaigns',
                label: taxonomy.t('nav.sendMessages.campaigns'),
                href: `${envAppPrefix}/${campaignsSegment}`,
                iconName: 'campaigns',
                tooltip: campaignsRenameTooltip,
              },
              {
                id: 'broadcasts',
                label: 'Broadcasts',
                href: `${envAppPrefix}/broadcasts`,
                iconName: 'broadcast',
              },
              {
                id: 'transactional',
                label: 'Transactional',
                href: `${envAppPrefix}/transactional`,
                iconName: 'transactional-api',
              },
              isAnonymousInAppEnabled
                ? {
                    id: 'anonymous',
                    label: 'Anonymous',
                    href: `${envAppPrefix}/anonymous`,
                    iconName: 'anonymous',
                  }
                : null,
              {
                id: 'message-library',
                label: 'Message Library',
                href: `${envAppPrefix}/message-library`,
                iconName: 'folder',
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Manage audience',
            items: [
              {
                id: 'people',
                label: taxonomy.t('nav.manageAudience.people'),
                href: `${envAppPrefix}/${peopleSegment}`,
                iconName: 'person',
              },
              {
                id: 'segments',
                label: 'Segments',
                href: `${envAppPrefix}/segments`,
                iconName: 'segment',
              },
            ],
          }
        : {
            title: 'Manage audience',
            items: [
              {
                id: 'people',
                label: taxonomy.t('nav.manageAudience.people'),
                href: `${envAppPrefix}/${peopleSegment}`,
                iconName: 'person',
              },
              ...generateObjectTypeRoutes(envAppPrefix, objectTypes ?? []),
              {
                id: 'segments',
                label: 'Segments',
                href: `${envAppPrefix}/segments`,
                iconName: 'segment',
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Create content',
            items: [
              {
                id: 'design-studio',
                label: 'Design Studio',
                iconName: 'design-studio-new',
                href: `${envPrefix}/design-studio/dashboard`,
              },
              {
                id: 'assets',
                label: 'Assets',
                href: `${envPrefix}/assets`,
                iconName: 'image',
                activeWhenUrls: [`${envPrefix}/assets`, `${envAppPrefix}/assets`, `${envPrefix}/design-studio/assets`],
              },
              {
                id: 'snippets',
                label: 'Snippets',
                // Hydra-native page — sits at `/snippets`, not under the
                // Journeys bundle's `/journeys/snippets` (which is now a
                // redirect target). See customerio/hydra route migration.
                href: `${envPrefix}/snippets`,
                iconName: 'liquid',
              },
            ],
          }
        : {
            title: 'Create content',
            items: [
              {
                id: 'design-studio',
                label: 'Design Studio',
                iconName: 'design-studio-new',
                href: `${envPrefix}/design-studio/dashboard`,
              },
              {
                id: 'assets',
                label: 'Assets',
                href: `${envPrefix}/assets`,
                iconName: 'image',
                activeWhenUrls: [`${envPrefix}/assets`, `${envAppPrefix}/assets`, `${envPrefix}/design-studio/assets`],
              },
              {
                id: 'layouts',
                label: 'Email Layouts',
                href: `${envAppPrefix}/layouts`,
                iconName: 'layout',
                isHiddenByDefault: true,
              },
              {
                id: 'snippets',
                label: 'Snippets',
                href: `${envPrefix}/snippets`,
                iconName: 'liquid',
                isHiddenByDefault: true,
              },
            ],
          },
      generateConfigureDataGroup({
        isBuilderPlan,
        isGeoFencingEnabled,
        envPrefix,
        envAppPrefix,
        collectionsShowPremiumLabel: auth.account?.planFeatures[PlanFeature.Collections].showPremiumLabel ?? true,
        hasPermission,
        hasAnyPermission,
      }),
      auth.account?.id === 1 && !isBuilderPlan
        ? {
            items: [
              {
                id: 'internal-tools',
                label: 'Internal Tools',
                href: '/internal-tools',
                iconName: 'a-b-test',
                isMoreMenuOnly: true,
              },
            ],
          }
        : { items: [] },
    ] as NavItemGroup[]
  )
    .map((group) => {
      const updatedGroup: NavItemGroup = { title: group.title, items: [] };

      for (const item of group.items ?? []) {
        if (item == null || item.isGreyedOut === true) {
          continue;
        }

        if ('children' in item && item.children != null) {
          const children = item.children.filter((child) => child != null && child.isGreyedOut !== true) as NavItem[];
          updatedGroup.items.push({ ...item, children } as NavItem);
        } else if (item != null) {
          updatedGroup.items.push(item as NavItem);
        }
      }

      return updatedGroup;
    })
    .filter((group) => group.items.length !== 0);
}

export function isNavItemActive(item: NavItem, currentUrl: string) {
  let isActive = false;

  if (item.activeWhenUrls != null) {
    isActive = item.activeWhenUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive && item.href != null) {
    isActive = currentUrl.startsWith(item.href);
  }

  if (isActive && item.activeExceptUrls != null) {
    isActive = !item.activeExceptUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive) {
    const isPipelinesRoute = currentUrl.includes('/pipelines');
    if (isPipelinesRoute && item.activateOnPipelinesRoutes != null && item.activateOnPipelinesRoutes.length > 0) {
      isActive = item.activateOnPipelinesRoutes.some((route) => route != null && currentUrl.includes(route));
    }
  }

  if (!isActive && item.children != null) {
    isActive = item.children.some((child) => isNavItemActive(child, currentUrl));
  }

  return isActive;
}

function generateObjectTypeRoutes(
  envAppPrefix: string,
  workspaceObjectTypes?: WorkspaceObjectType[],
  isGreyedOut = false,
) {
  const objectTypes =
    workspaceObjectTypes?.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }

      if (a.name > b.name) {
        return 1;
      }

      return 0;
    }) ?? [];

  if (objectTypes.length === 0) {
    return [
      {
        id: 'objects',
        label: 'Custom Objects',
        href: `${envAppPrefix}/objects`,
        iconName: 'object',
        isGreyedOut,
      },
    ];
  }

  if (objectTypes.length <= 3) {
    return objectTypes.map((objectType) => {
      const isValidIcon = (icon: string | undefined): icon is IconName => {
        return icon != null && !isEmptyString(icon.trim()) && (iconNames as readonly string[]).includes(icon);
      };

      const icon: IconName = isValidIcon(objectType.icon) ? objectType.icon : 'object';

      return {
        id: `object-types_${objectType.id}`,
        label: objectType.name,
        href: `${envAppPrefix}/objects/${objectType.slug}`,
        iconName: icon,
        isGreyedOut,
      };
    });
  }

  return [
    {
      id: 'custom-objects',
      label: 'Custom Objects',
      iconName: 'object',
      isGreyedOut,
      children: objectTypes.map((objectType) => ({
        id: `object-types_${objectType.id}`,
        label: objectType.name,
        href: `${envAppPrefix}/objects/${objectType.slug}`,
      })),
    },
  ];
}
